import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TreePine, Bell, CheckCircle, MapPinned, Sprout, Landmark } from 'lucide-react';

export default function HomeTab({
  displayedParks: displayedParksProp,
  inAppNotifications = [],
  unreadNotificationCount = 0,
  onMarkAsRead,
  onMarkAllRead,
  onRefreshNotifications,
  onQuickParkFilter,
  onInventoryDetail,
  onFacilityDetail,
  onIrrigationDetail,
}) {
  const displayedParks = Array.isArray(displayedParksProp) ? displayedParksProp : [];
  // Kompakt: yalnızca OKUNMAMIŞ bildirimler gösterilir (okununca/görev açılınca/silinince düşer);
  // tamamı üstteki 🔔 zil panelinden görülebilir.
  const unreadList = (Array.isArray(inAppNotifications) ? inAppNotifications : []).filter(n => !n?.is_read);
  const MAX_SHOWN = 6;
  const shownNotifications = unreadList.slice(0, MAX_SHOWN);
  const moreCount = Math.max(0, unreadList.length - MAX_SHOWN);
  const hasNotifications = unreadList.length > 0;
  const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const totalArea = Math.round(
    displayedParks.reduce((sum, park) => sum + toNumber(park?.area), 0)
  );
  const totalGrassArea = Math.round(
    displayedParks.reduce((sum, park) => sum + toNumber(park?.grass_area), 0)
  );

  const inventorySummary = {
    lightPoles: displayedParks.reduce((sum, park) => sum + toNumber(park?.light_pole_count), 0),
    benches: displayedParks.reduce((sum, park) => sum + toNumber(park?.bench_count), 0),
    trashBins: displayedParks.reduce((sum, park) => sum + toNumber(park?.trash_bin_count), 0),
    gazebos: displayedParks.reduce((sum, park) => sum + toNumber(park?.gazebo_count), 0),
  };

  const facilitySummary = {
    basketball: displayedParks.filter((park) => Boolean(park?.has_basketball_court)).length,
    football: displayedParks.filter((park) => Boolean(park?.has_football_field)).length,
    volleyball: displayedParks.filter((park) => Boolean(park?.has_volleyball_court)).length,
    tennis: displayedParks.filter((park) => Boolean(park?.has_tennis_court)).length,
    irrigationAutomatic: displayedParks.filter((park) => String(park?.irrigation_system || '').toLowerCase() === 'otomatik').length,
    irrigationManual: displayedParks.filter((park) => String(park?.irrigation_system || '').toLowerCase() === 'manuel').length,
    irrigationHose: displayedParks.filter((park) => String(park?.irrigation_system || '').toLowerCase() === 'hortumla').length,
    irrigationNone: displayedParks.filter((park) => {
      const irrigation = String(park?.irrigation_system || '').toLowerCase();
      return !irrigation || irrigation === 'yok';
    }).length,
  };

  const formatNotificationTime = (ts) => {
    if (!ts) return '';
    try {
      const d = typeof ts === 'string' ? new Date(ts) : ts;
      return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return String(ts).slice(0, 16);
    }
  };

  const openFilteredParks = (quickFilter) => {
    if (typeof onQuickParkFilter === "function") {
      onQuickParkFilter(quickFilter);
    }
  };

  // Envanter/tesis/sulama kartları: hangi parkta ne kadar bulunduğunu (bölge kırılımlı)
  // gösteren detay modalını açar. Yönetici panelindeki dökümle aynı, ancak yalnızca
  // görevlinin sorumlu olduğu parklara (displayedParks) göre filtrelidir.
  const openInventoryDetail = (title, field) => {
    if (typeof onInventoryDetail === "function") {
      onInventoryDetail(title, field);
    }
  };
  const openFacilityDetail = (title, field) => {
    if (typeof onFacilityDetail === "function") {
      onFacilityDetail(title, field);
    }
  };
  const openIrrigationDetail = (title, type) => {
    if (typeof onIrrigationDetail === "function") {
      onIrrigationDetail(title, type);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold">Hoş Geldiniz</h2>
      </div>
      <p className="text-xs text-gray-500 -mt-4">
        Üstteki park kartlarına tıklayarak Parklar sekmesine filtreli geçiş yapabilir;
        envanter ve tesis kartlarına tıklayarak hangi parkta ne kadar bulunduğunu (bölge kırılımlı) görebilirsiniz.
      </p>

      {/* PG Park İstatistikleri */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-blue-200 bg-blue-50/60 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openFilteredParks({ kind: "all", label: "Tüm Parklar" })}>
          <CardContent className="p-4">
            <p className="text-sm text-blue-700 font-medium">Toplam Park</p>
            <p className="text-2xl font-bold text-blue-900 mt-1">{displayedParks.length}</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/60 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openFilteredParks({ kind: "all", label: "Toplam Alan (Tüm Parklar)" })}>
          <CardContent className="p-4">
            <p className="text-sm text-emerald-700 font-medium">Toplam Alan</p>
            <p className="text-2xl font-bold text-emerald-900 mt-1">{totalArea.toLocaleString('tr-TR')} m²</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/60 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openFilteredParks({ kind: "grass_positive", label: "Çim Alanı Olan Parklar" })}>
          <CardContent className="p-4">
            <p className="text-sm text-green-700 font-medium">Toplam Çim Alanı</p>
            <p className="text-2xl font-bold text-green-900 mt-1">{totalGrassArea.toLocaleString('tr-TR')} m²</p>
          </CardContent>
        </Card>
      </div>

      {/* PG Envanter Özeti */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPinned className="h-5 w-5 text-indigo-600" />
            Envanter Özeti (Sorumlu Olduğum Parklar)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border bg-slate-50 p-3 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => openInventoryDetail("Elektrik Direği", "light_pole_count")}>
              <p className="text-xs text-slate-600">Elektrik Direği</p>
              <p className="text-xl font-bold text-slate-900">{inventorySummary.lightPoles}</p>
            </div>
            <div className="rounded-lg border bg-slate-50 p-3 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => openInventoryDetail("Bank", "bench_count")}>
              <p className="text-xs text-slate-600">Bank</p>
              <p className="text-xl font-bold text-slate-900">{inventorySummary.benches}</p>
            </div>
            <div className="rounded-lg border bg-slate-50 p-3 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => openInventoryDetail("Çöp Kovası", "trash_bin_count")}>
              <p className="text-xs text-slate-600">Çöp Kovası</p>
              <p className="text-xl font-bold text-slate-900">{inventorySummary.trashBins}</p>
            </div>
            <div className="rounded-lg border bg-slate-50 p-3 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => openInventoryDetail("Kamelya", "gazebo_count")}>
              <p className="text-xs text-slate-600">Kamelya</p>
              <p className="text-xl font-bold text-slate-900">{inventorySummary.gazebos}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PG Tesis Durumu */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Landmark className="h-5 w-5 text-orange-600" />
            Tesis Durumu
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Spor Sahaları</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg border bg-orange-50 p-3 cursor-pointer hover:bg-orange-100 transition-colors" onClick={() => openFacilityDetail("Basketbol Sahası", "has_basketball_court")}><p className="text-xs text-orange-700">Basketbol</p><p className="text-lg font-bold text-orange-900">{facilitySummary.basketball}</p></div>
              <div className="rounded-lg border bg-orange-50 p-3 cursor-pointer hover:bg-orange-100 transition-colors" onClick={() => openFacilityDetail("Futbol Sahası", "has_football_field")}><p className="text-xs text-orange-700">Futbol</p><p className="text-lg font-bold text-orange-900">{facilitySummary.football}</p></div>
              <div className="rounded-lg border bg-orange-50 p-3 cursor-pointer hover:bg-orange-100 transition-colors" onClick={() => openFacilityDetail("Voleybol Sahası", "has_volleyball_court")}><p className="text-xs text-orange-700">Voleybol</p><p className="text-lg font-bold text-orange-900">{facilitySummary.volleyball}</p></div>
              <div className="rounded-lg border bg-orange-50 p-3 cursor-pointer hover:bg-orange-100 transition-colors" onClick={() => openFacilityDetail("Tenis Kortu", "has_tennis_court")}><p className="text-xs text-orange-700">Tenis</p><p className="text-lg font-bold text-orange-900">{facilitySummary.tennis}</p></div>
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
              <Sprout className="h-4 w-4 text-green-600" />
              Sulama Sistemi
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg border bg-green-50 p-3 cursor-pointer hover:bg-green-100 transition-colors" onClick={() => openIrrigationDetail("Otomatik Sulama", "otomatik")}><p className="text-xs text-green-700">Otomatik</p><p className="text-lg font-bold text-green-900">{facilitySummary.irrigationAutomatic}</p></div>
              <div className="rounded-lg border bg-green-50 p-3 cursor-pointer hover:bg-green-100 transition-colors" onClick={() => openIrrigationDetail("Manuel Sulama", "manuel")}><p className="text-xs text-green-700">Manuel</p><p className="text-lg font-bold text-green-900">{facilitySummary.irrigationManual}</p></div>
              <div className="rounded-lg border bg-green-50 p-3 cursor-pointer hover:bg-green-100 transition-colors" onClick={() => openIrrigationDetail("Hortumla Sulama", "hortumla")}><p className="text-xs text-green-700">Hortumla</p><p className="text-lg font-bold text-green-900">{facilitySummary.irrigationHose}</p></div>
              <div className="rounded-lg border bg-green-50 p-3 cursor-pointer hover:bg-green-100 transition-colors" onClick={() => openIrrigationDetail("Sulama Yok", "yok")}><p className="text-xs text-green-700">Yok</p><p className="text-lg font-bold text-green-900">{facilitySummary.irrigationNone}</p></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bildirimlerim — yalnızca okunmamışlar (kompakt); tamamı 🔔 zil panelinde */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-blue-600" />
              Bildirimlerim
              {unreadList.length > 0 && (
                <span className="text-sm font-normal text-blue-600">({unreadList.length} okunmamış)</span>
              )}
            </span>
            {hasNotifications && typeof onMarkAllRead === 'function' && (
              <Button size="sm" variant="ghost" className="text-xs" onClick={onMarkAllRead}>
                <CheckCircle className="h-4 w-4 mr-1" />
                Hepsini okundu
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!hasNotifications ? (
            <div className="text-center py-4 text-gray-400">
              <Bell className="h-8 w-8 mx-auto mb-1 opacity-30" />
              <p className="text-sm">Yeni bildirim yok</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {shownNotifications.map((n) => (
                <div key={n.id} className="p-3 rounded-lg border bg-blue-50/50 border-blue-200">
                  <p className="font-medium text-gray-900">{n.title || 'Bildirim'}</p>
                  <p className="text-sm text-gray-700 mt-1">{n.message}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-500">{formatNotificationTime(n.timestamp)}</span>
                    {typeof onMarkAsRead === 'function' && (
                      <Button size="sm" variant="ghost" className="text-xs" onClick={() => onMarkAsRead(n.id)}>
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Okundu
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {moreCount > 0 && (
                <p className="text-xs text-gray-400 text-center pt-1">+{moreCount} bildirim daha (üstteki 🔔 zilden görün)</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Atanan Parklar */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TreePine className="h-5 w-5 text-green-600" />
            Sorumlu Olduğum Parklar ({displayedParks.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {displayedParks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <TreePine className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Henüz size atanmış park bulunmuyor</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {displayedParks.map(park => (
                <div key={park.id} className="p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                  <h4 className="font-semibold text-gray-900">{park.name}</h4>
                  <p className="text-sm text-gray-600">{park.neighborhood || "Mahalle belirtilmemiş"}</p>
                  {park.latitude && park.longitude && (
                    <p className="text-xs text-gray-500 mt-1">
                      📍 {park.latitude.toFixed(4)}, {park.longitude.toFixed(4)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
